import type { ReactNode } from 'react';
import { useOnline, useQueuedWrites, useJustSynced } from './useConnection';
import { useRefusedPhotoCount } from '../../lib/upload-queue';
import type { Tone } from './Badge';

/**
 * What the tech needs to know about the connection, and nothing else.
 *
 * The app has queued writes offline since M0 and never said so. A tech in a crawlspace
 * tapped "Finish this job", the button read "Finishing…" forever, and nothing on screen
 * distinguished that from a hung app. The reasonable response is to tap again, or to
 * decide it didn't work and write the job up on paper.
 *
 * Four states, one sentence each, no jargon — "sync", "queue" and "pending" are all words
 * that mean nothing on a porch:
 *
 *   offline             → what still works, and what happens to what you do
 *   online with a queue → it's going out now, and how much is left
 *   a photo refused     → it is still here, and it needs a person
 *   just finished       → everything got through (then it goes away)
 *
 * ⭐ **A refused WRITE deliberately has no bar here, and the asymmetry with photos is the
 * point.** A refused photo's recovery lives on the job it belongs to, which may be three
 * stops back — so it needs a bar to send the tech there. A refused write's recovery is
 * `UnsentWorkPanel`, rendered by the same shell immediately below this component; a bar
 * announcing it directly above the thing itself is noise. What the refusal *does* change here
 * is the last state: `useJustSynced` is false while any refused work is on the phone, durable
 * log included, so "everything got through" cannot fire over a payment the server refused.
 */
export function ConnectionBanner() {
  const online = useOnline();
  const queued = useQueuedWrites();
  const refused = useRefusedPhotoCount();
  const justSynced = useJustSynced();

  if (!online) {
    return (
      <Bar tone="warn">
        <strong className="font-semibold">No signal.</strong> Keep working — everything
        you do is saved on this phone and sends itself when you're back.
        {queued > 0 ? ` ${queued} waiting.` : ''}
      </Bar>
    );
  }

  if (queued > 0) {
    return (
      <Bar tone="brand">
        Sending {queued} {queued === 1 ? 'thing' : 'things'} you did while you were out of
        signal…
      </Bar>
    );
  }

  /*
   * A photo the server refused is the one thing here that a network will never fix, and it
   * is invisible from anywhere but the job it belongs to. Persistent on purpose: it is the
   * only warning a tech gets that a photograph they took is not going to be on the record.
   */
  if (refused > 0) {
    return (
      <Bar tone="warn">
        {refused === 1 ? 'A photo wasn’t' : `${refused} photos weren’t`} accepted.{' '}
        {refused === 1 ? 'It’s' : 'They’re'} still on this phone — open the job to see
        why.
      </Bar>
    );
  }

  if (justSynced) {
    return <Bar tone="success">Everything you did offline has gone through.</Bar>;
  }

  return null;
}

/** Full-bleed strip in the shared tone vocabulary — `badge--*` carries the tint. */
function Bar({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    // aria-live so a screen reader announces losing signal rather than leaving it to be
    // noticed. Not `assertive` — it is important, not an emergency.
    <p aria-live="polite" className={`badge--${tone} px-4 py-2 text-sm`}>
      {children}
    </p>
  );
}
